import { LoginStorageModel, LoginOktaStorageModel } from '../../login/model';
import axios from 'axios';
import { getEnv } from '../env/env.manager';
import { getStorage } from '../storage/storage.manager';
// import { AbstractStorageService } from '../contracts/storage.interface';

const env = getEnv();
const baseURL = env.baseURL;

const instance = axios.create({
  baseURL: baseURL,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

const storageProvider = getStorage();
const storageInstance = storageProvider.getInstance(LoginStorageModel);
const storageInstanceOkta = storageProvider.getInstance(LoginOktaStorageModel);

/// for multiple parallel requests
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

instance.interceptors.response.use(response => response, async (error) => {
  const originalRequest = error.config;

  if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._skipAuthRefresh) {

    // --- OKTA path ---
    const oktaInfo = await storageInstanceOkta.find((item: LoginOktaStorageModel): boolean =>
      Boolean(item?.oktaToken && !['undefined', 'null', ''].includes(item.oktaToken))
    );

    if (oktaInfo) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['x-okta-token'] = token;
          return instance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await instance.post('/accounts/refreshOktaToken', {
          refreshToken: oktaInfo.oktaRefreshToken
        });

        const newToken = res.data?.accessToken;
        if (!newToken) throw new Error('No access token returned');

        // Update storage
        await storageInstanceOkta.upsertOne(() => true, {
          ...oktaInfo,
          oktaToken: newToken,
          oktaRefreshToken: res.data.refreshToken ?? oktaInfo.oktaRefreshToken,
          oktaTokenExpiresAt: Date.now() + (res.data.expiresIn ?? 3600) * 1000,
        });

        processQueue(null, newToken);
        originalRequest.headers['x-okta-token'] = newToken;
        return instance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return redirectToLogin();
      } finally {
        isRefreshing = false;
      }
    }

    // --- Internal JWT path (unchanged) ---
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['X-Token'] = 'Bearer ' + token;
        return instance(originalRequest);
      }).catch(err => err);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return new Promise((resolve, reject) => {
      storageInstance.find((item: LoginStorageModel) => item?.modelGuid === 'token')
        .then((userInfo) => {
          if (!userInfo) return redirectToLogin();

          instance.post('/accounts/refresh-token', {
            refreshToken: userInfo.refreshToken,
            username: userInfo.username,
            grantType: 'refresh_token',
            clientId: 'website'
          }).then(({ data }) => {
            storageInstance.upsertOne(
              x => x.id === data.response.id,
              { ...userInfo, token: data.response.token, expires_on: data.response.expires_on }
            );
            originalRequest.headers['X-Token'] = `Bearer ${data.response.token}`;
            processQueue(null, data.response.token);
            resolve(instance(originalRequest));
          }).catch(err => {
            processQueue(err, null);
            reject(err);
            redirectToLogin();
          }).finally(() => { isRefreshing = false; });
        }).catch(() => redirectToLogin());
    });
  }

  return Promise.reject(error);
});

function redirectToLogin() {
  isRefreshing = false;
  storageInstance.clear();
  storageInstanceOkta.clear();
  window.location.href = '/login';
  // console.log('Redirect to login');
  // how to dispatch action from here to reset state
  // LOGOUT action ...
}




instance.interceptors.request.use(async (config) => {
  const oktaInfo = await storageInstanceOkta.find((item: LoginOktaStorageModel) => {
    if (item && item.oktaToken !== 'undefined' && item.oktaToken !== 'null' && item.oktaToken !== '' && item.oktaToken !== undefined) {
      return true;
    }
    return false;
  });

  const userInfo = await storageInstance.find((item: LoginStorageModel) => {
    if (item && item.modelGuid === 'token') {
       return true;
    }
    return false;
  });

  config.headers['X-Token'] = userInfo ? `Bearer ${userInfo.token}` : '';
  config.headers['X-Refresh-Token'] = userInfo ? `Bearer ${userInfo.refreshToken}` : '';
  config.headers['Accept-Language'] = userInfo ? userInfo.languageCode : navigator.language;


  if (oktaInfo) {
    config.headers['x-okta-token'] = oktaInfo ? oktaInfo.oktaToken : '';
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

export default instance;
